import React, { useState } from "react";
import { IconClose } from "../../../components/Ui/Icon";
import LoadingOverlay from "../../../components/Ui/LoadingOverlay";
import { adminCreateVideo, adminUpdateVideo } from "../../../services/video";
import { getS3Presign } from "../../../store/upload";

const Create = ({ togglePopup, onSubmited, data }) => {
  const [loading, setLoading] = useState(false);
  const [formValue, setFormValue] = useState(
    data
      ? { title: data.title || "", url: data.url || "", _id: data._id }
      : { title: "", url: "" }
  );
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValue((preValue) => ({
      ...preValue,
      [`${name}`]: value,
    }));
  };

  const handleUploadVideo = async (file) => {
    if (!file) return;
    try {
      setUploading(true);
      setUploadPct(0);
      const presign = await getS3Presign({ filename: file.name, contentType: file.type, folderPath: "uploads/videos" });
      if (!presign?.url) throw new Error("No presigned URL");
      // Use XHR to get progress events
      const xhr = new XMLHttpRequest();
      const putPromise = new Promise((resolve, reject) => {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const pct = Math.round((e.loaded / e.total) * 100);
            setUploadPct(pct);
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve(true);
          else reject(new Error(`Upload failed with status ${xhr.status}`));
        };
        xhr.onerror = () => reject(new Error("Upload network error"));
        xhr.open("PUT", presign.url);
        xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
        xhr.send(file);
      });
      await putPromise;
      setFormValue((s) => ({ ...s, url: presign.publicUrl }));
    } catch (e) {
      // swallow, UI stays
    } finally {
      setUploading(false);
      setTimeout(() => setUploadPct(0), 500);
    }
  };

  const onSubmitHandler = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (formValue._id) {
        await adminUpdateVideo(formValue._id, { title: formValue.title, url: formValue.url });
      } else {
        await adminCreateVideo({ title: formValue.title, url: formValue.url });
      }
      onSubmited?.(formValue);
      togglePopup?.();
    } catch (e) {}
    setLoading(false);
  };

  return (
    <div>
      {loading && <LoadingOverlay />}
      <div className="modal flex overflow-y-auto overflow-x-hidden fixed inset-0 bg-gray-700 bg-opacity-75 z-[6] justify-center items-center w-full max-h-full">
        <div className="relative p-4 w-full max-w-2xl max-h-full" onClick={(e) => e.stopPropagation()}>
          <div className="relative bg-white rounded-lg shadow dark:bg-gray-700">
            <div className="flex items-center justify-between p-4 md:p-5 border-b rounded-t dark:border-gray-600">
              <h3 className="text-xl font-semibold text-black dark:text-white">
                {data ? "Edit Video" : "Create Video"}
              </h3>
              <button
                type="button"
                className="z-[7] text-gray-400 bg-transparent hover:bg-gray-200 hover:text-black rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white"
                onClick={togglePopup}
              >
                <IconClose />
                <span className="sr-only">Close modal</span>
              </button>
            </div>
            <div className="p-4 md:p-5 space-y-4">
              <form onSubmit={(e) => onSubmitHandler(e)} className="grid gap-4 sm:grid-cols-2 sm:gap-6 [&_input]:outline-none">
                <div className="sm:col-span-2">
                  <label htmlFor="title" className="block mb-2 text-sm font-medium text-black dark:text-white text-start">Title</label>
                  <input
                    type="text"
                    name="title"
                    value={formValue.title}
                    onChange={handleChange}
                    placeholder="Title"
                    className="shadow-sm bg-gray-50 border border-gray-300 text-black sm:text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="url" className="block mb-2 text-sm font-medium text-black dark:text-white text-start">Video URL</label>
                  <input
                    type="text"
                    name="url"
                    value={formValue.url}
                    onChange={handleChange}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className="shadow-sm bg-gray-50 border border-gray-300 text-black sm:text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  />
                  <div className="mt-2">
                    <label className="inline-flex items-center justify-center px-3 py-2 rounded border bg-white hover:bg-gray-50 cursor-pointer">
                      <input
                        type="file"
                        accept="video/*"
                        className="hidden"
                        onChange={(e) => handleUploadVideo(e.target.files?.[0])}
                      />
                      {uploading ? `Uploading ${uploadPct}%` : "Upload to S3"}
                    </label>
                    {uploading && (
                      <div className="mt-2 w-full max-w-xs">
                        <div className="w-full h-2 bg-gray-200 rounded">
                          <div
                            className="h-2 bg-blue-600 rounded"
                            style={{ width: `${uploadPct}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex justify-center sm:col-span-2">
                  <button className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center">
                    Submit
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Create;


